import { zodResolver } from '@hookform/resolvers/zod';
import { UsersRound } from 'lucide-react';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { PasswordInput } from '@/components/forms/password-input-simple';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Form } from '@/components/ui/form';
import { MEDIA_URL } from '@/config';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import type { Team } from '@/types';

import { useJoinTeam } from '../api/joinTeam';

import { TeamSearch } from './team-search';

interface JoinTeamProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const formSchema = z.object({
  password: z.string().min(1, 'Password is required'),
});

type JoinTeamFormValues = z.infer<typeof formSchema>;
export const JoinTeam: React.FC<JoinTeamProps> = ({ open, setOpen }) => {
  const isMobile = useIsMobile();
  const [currentStep, setCurrentStep] = React.useState(1);
  const [selectedTeam, setSelectedTeam] = React.useState<Team>();
  const headerTitle =
    currentStep === 1
      ? 'Join a team'
      : selectedTeam
        ? `Join ${selectedTeam.team_name}`
        : 'Join a team';
  const headerDescription =
    currentStep === 1 ? 'Search for your team' : 'Enter your team password';

  React.useEffect(() => {
    if (open) {
      setCurrentStep(1);
      setSelectedTeam(undefined);
    }
  }, [open]);

  const renderHeader = () => (
    <div className="flex flex-col items-center gap-2">
      {currentStep === 2 && selectedTeam ? (
        <div className="flex items-center gap-2">
          <Avatar className="size-10">
            <AvatarImage
              src={`${MEDIA_URL}/images/uploads/${selectedTeam.team_photo}`}
              alt={selectedTeam.team_name}
            />
            <AvatarFallback>
              <UsersRound className="text-primary" />
            </AvatarFallback>
          </Avatar>
        </div>
      ) : (
        <div
          className="flex size-11 shrink-0 items-center justify-center rounded-full border"
          aria-hidden="true"
        >
          <UsersRound className="text-primary" />
        </div>
      )}
    </div>
  );

  if (!isMobile) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          {renderHeader()}
          <DialogHeader>
            <DialogTitle className="sm:text-center">{headerTitle}</DialogTitle>
            <DialogDescription className="sm:text-center">
              {headerDescription}
            </DialogDescription>
          </DialogHeader>
          <JoinTeamForm
            setOpen={setOpen}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            setSelectedTeam={setSelectedTeam}
            selectedTeam={selectedTeam}
            isDrawer={isMobile}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent>
        <DrawerHeader className="text-left">
          {renderHeader()}
          <DrawerTitle className="text-center">{headerTitle}</DrawerTitle>
          <DrawerDescription className="text-center">
            {headerDescription}
          </DrawerDescription>
        </DrawerHeader>
        <JoinTeamForm
          className="px-4"
          setOpen={setOpen}
          setCurrentStep={setCurrentStep}
          currentStep={currentStep}
          setSelectedTeam={setSelectedTeam}
          selectedTeam={selectedTeam}
          isDrawer={isMobile}
        />
        <DrawerFooter className="pt-2">
          {currentStep == 2 ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSelectedTeam(undefined);
                setCurrentStep(1);
              }}
            >
              Back
            </Button>
          ) : (
            <DrawerClose asChild>
              <Button variant="outline">Cancel</Button>
            </DrawerClose>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

interface JoinTeamFormProps extends React.ComponentProps<'form'> {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  selectedTeam: Team | undefined;
  setSelectedTeam: React.Dispatch<React.SetStateAction<Team | undefined>>;
  isDrawer: boolean;
}

function JoinTeamForm({
  className,
  setOpen,
  currentStep,
  setCurrentStep,
  setSelectedTeam,
  selectedTeam,
  isDrawer,
}: JoinTeamFormProps) {
  const joinTeamMutation = useJoinTeam();
  const form = useForm<JoinTeamFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
    },
  });
  React.useEffect(() => {
    if (currentStep === 2) {
      form.setFocus('password');
    }
  }, [currentStep, form]);

  const onSubmit = async (values: JoinTeamFormValues) => {
    if (!selectedTeam) {
      toast.error('No team selected');
      return;
    }
    joinTeamMutation.mutate(
      {
        data: {
          ...values,
          id: selectedTeam.id,
        },
      },
      {
        onSuccess: (team) => {
          toast.success(`Successfully joined ${team.team_name}!`);
          setOpen(false);
        },
        onError: (error: any) => {
          if (error.data?.fields) {
            const fieldErrors = error.data.fields;
            Object.entries(fieldErrors).forEach(([field, message]) => {
              form.setError(field as keyof JoinTeamFormValues, {
                type: 'server',
                message: message as string,
              });
            });
          }
        },
      },
    );
  };

  const { isValid, isDirty } = form.formState;
  const isLoading = joinTeamMutation.isPending;

  return (
    <Form {...form}>
      <form
        className={cn('grid items-start gap-4', className)}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        {currentStep === 1 && (
          <TeamSearch
            isDrawer={isDrawer}
            onTeamSelect={(team) => {
              setSelectedTeam(team);
              setCurrentStep(2);
            }}
          />
        )}
        {currentStep === 2 && (
          <>
            <PasswordInput control={form.control} name="password" label="" />
            {isDrawer ? (
              <Button
                type="submit"
                disabled={isLoading || !isDirty || !isValid}
              >
                {isLoading ? 'Joining...' : 'Join Team'}
              </Button>
            ) : (
              <div className="flex justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSelectedTeam(undefined);
                    setCurrentStep(1);
                  }}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading || !isDirty || !isValid}
                >
                  {isLoading ? 'Joining...' : 'Join Team'}
                </Button>
              </div>
            )}
          </>
        )}
      </form>
    </Form>
  );
}
